import { useT, useUiStore } from '@/store';
import { useWorklist } from '@/hooks/useInvoicing';
import { Panel, ErrorState, SkeletonRows } from '@/components/ui/Panel';
import { eurExact } from '@/lib/format';

/**
 * One queue: money this carrier has earned and nobody has collected.
 *
 * The console had ten measurement panels, each correct and each waiting for
 * somebody to come and look at it. The owner of a five-truck carrier drives
 * one of the trucks; he is not going to open ten tabs. The digests that reach
 * his phone are the push half of this — they say something changed. This is
 * the pull half: what to do first, with the euros beside it.
 *
 * NOTHING IS RE-DERIVED HERE. core/worklist.py ranks the rows and totals
 * them, and this renders that answer. A panel that re-sorted or re-summed
 * would be a second opinion about the same euros, and the two would drift the
 * first time either rule changed — which is exactly how a carrier ends up
 * with two screens disagreeing about what he is owed.
 *
 * THREE THINGS THIS MUST NOT DO
 * ------------------------------
 *  1. Print an unknown amount as 0,00 €. The server sends `amount_eur: null`
 *     when nothing behind a row could be priced, and that null is the whole
 *     reason it exists — a zero reads as work given away free.
 *  2. Present the total as the whole exposure. When any row is unpriced the
 *     header says so, because the sum covers only the rows that had a figure.
 *  3. Read as a complete to-do list. It is money only. Service and document
 *     deadlines are real and expensive and are NOT here — mixing them into a
 *     euro ranking would need a price for a grounded truck that nobody has
 *     stated. The footer says so rather than letting the omission be found.
 */

/** The codebase's "no number at all" — never a blank cell, never a zero. */
const EM_DASH = '—';

/**
 * Literal keys, one per effort and one per kind, rather than
 * `worklist.effort.${row.effort}`.
 *
 * tools/check_i18n.py reads source TEXT, so a key only ever reached through a
 * variable is invisible to it — and a key dropped from the bundle would then
 * reach a carrier as the raw string "worklist.effort.phone_call". The same
 * device ServicePanel, FuelPanel and DowntimePanel all use.
 */
const EFFORT_KEYS = {
  one_click: 'worklist.effort.one_click',
  send_document: 'worklist.effort.send_document',
  phone_call: 'worklist.effort.phone_call',
  data_entry: 'worklist.effort.data_entry',
};

/** Blocker labels come from the console's own vocabulary — the same strings
 *  the billing tab's "Į sąskaitą nepateko" list uses. A blocker named two
 *  ways is one the carrier has to match up himself before he can act. */
const REASON_KEYS = {
  no_client: 'billing.excluded.no_client',
  no_pod: 'billing.excluded.no_pod',
  unpriced: 'billing.excluded.unpriced',
  weight_estimated: 'billing.excluded.weight_estimated',
  already_invoiced: 'billing.excluded.already_invoiced',
};

function rowLabel(t, row) {
  if (row.kind === 'invoice_ready') return t('worklist.kind.invoice_ready');
  // A send row and a chase row are both about one client, so the client's
  // name alone would put two different jobs on screen under the same
  // heading — one of them a phone call the carrier must not make yet. The
  // send row says which it is; the chase row keeps the bare name it has
  // always had.
  if (row.kind === 'send_invoice') return t('worklist.kind.send_invoice', { name: row.subject });
  if (row.kind === 'chase_client') return row.subject;
  // A reason with no translation falls back to its own key rather than to a
  // blank cell: a missing string must cost a tidy label, never a whole row of
  // money going unmentioned. core/unbilled_digest._label() states the same
  // rule for the Telegram side.
  return REASON_KEYS[row.subject] ? t(REASON_KEYS[row.subject]) : row.subject;
}

function Amount({ row, t }) {
  if (!row.amount_known) {
    return <span className="text-dim italic">{t('worklist.unknownAmount')}</span>;
  }
  return <span className="font-bold tabular-nums">{eurExact(row.amount_eur)}</span>;
}

function Row({ row, t, onOpen }) {
  const counted = row.kind === 'chase_client' || row.kind === 'send_invoice'
    ? t('worklist.invoices', { n: row.count })
    : t('worklist.loads', { n: row.count });

  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-line py-2 last:border-0">
      <span className="min-w-[7.5rem] text-right">
        <Amount row={row} t={t} />
      </span>
      <span className="flex-1 min-w-[12rem]">
        <span className="font-medium">{rowLabel(t, row)}</span>
        <span className="text-dim"> · {counted}</span>
        {/* Only on a debt, and only when it is actually late — the server
            sends 0 for a client whose overdue pile is empty, and "vėluoja
            0 d." is not a sentence. */}
        {row.oldest_overdue_days > 0 ? (
          <span className="text-amber"> · {t('worklist.overdueBy', { n: row.oldest_overdue_days })}</span>
        ) : null}
        {/* The disclosure that keeps the amount honest at row level: this
            euro figure covers only the priced loads behind it. Without it the
            carrier plans around a number short by exactly the loads nobody
            could price — which are the ones that sit longest. */}
        {row.unpriced_count > 0 && row.amount_known ? (
          <span className="text-dim"> · {t('worklist.unpricedNote', { n: row.unpriced_count })}</span>
        ) : null}
        {/* V-51. The waiting half of the ready row, named. One press of
            the invoice button collects both, so the amount is their sum —
            and a carrier comparing it against the draft's haulage lines has
            to be able to see why the two differ.

            No null branch: the server sends a count and a total together or
            neither, because find_billable_dwell() keeps a stop that closed
            before any rate existed out of the lines entirely. */}
        {row.dwell_count > 0 && row.dwell_eur != null ? (
          <span className="text-structure">
            {' · '}
            {t('worklist.dwellIncluded', { n: row.dwell_count, eur: eurExact(row.dwell_eur) })}
          </span>
        ) : null}
      </span>
      <span className="text-[10.5px] uppercase tracking-[0.03em] text-dim">
        {t(EFFORT_KEYS[row.effort]) || EM_DASH}
      </span>
      <button type="button" className="btn !px-2.5 !py-1 !text-[11px]" onClick={onOpen}>
        {t('worklist.go')}
      </button>
    </li>
  );
}

export function WorklistPanel() {
  const t = useT();
  // setView, and the server's `where` is one of the store's own VIEWS ids
  // — 'billing'. setView falls back to 'dispatcher' for anything it does
  // not recognise, so a value this panel invented would silently send
  // somebody to the map instead of to their money.
  const setView = useUiStore((s) => s.setView);
  const { data, isLoading, isError, error } = useWorklist();

  const rows = data?.actions ?? [];

  // Nothing owed is not a panel. An empty "money you have not collected" box
  // on the landing screen every day is how a carrier learns to stop reading
  // the landing screen — and unlike the checklist above it, there is no
  // action it could offer instead.
  if (!isLoading && !isError && rows.length === 0) return null;

  return (
    <Panel title={t('worklist.title')}>
      {isError ? <ErrorState error={error} /> : null}
      {isLoading ? (
        <SkeletonRows rows={3} />
      ) : (
        <>
          <p className="mb-2 text-[11px] leading-snug text-dim">{t('worklist.intro')}</p>
          <p className="mb-3 text-[13px] font-bold">
            {t('worklist.total', { amount: eurExact(data.total_eur) })}
          </p>
          {data.unknown_amount_count > 0 ? (
            <p className="mb-3 text-[10.5px] leading-snug text-amber">
              {t('worklist.totalPartial', { n: data.unknown_amount_count })}
            </p>
          ) : null}

          <ul className="text-[11.5px]">
            {rows.map((row) => (
              <Row key={row.id} row={row} t={t} onOpen={() => setView(row.where)} />
            ))}
          </ul>

          {/* Cap-and-disclose. The cap cuts the SMALLEST rows, so what it
              leaves out is exactly what the carrier would otherwise never
              see — and both dimensions are named, because a pile he cannot
              size is one he cannot plan around. */}
          {data.omitted_actions > 0 ? (
            <p className="mt-2 text-[10.5px] leading-snug text-dim">
              {t('worklist.omitted', {
                shown: rows.length,
                n: data.omitted_actions,
                amount: eurExact(data.omitted_eur),
              })}
            </p>
          ) : null}

          <p className="mt-3 text-[10.5px] leading-snug text-dim">{t('worklist.scopeNote')}</p>
        </>
      )}
    </Panel>
  );
}
